import React from 'react';
import PropTypes from 'prop-types';

// =============================================================================
// EntityBreakdownCard — v2 React primitive for the "entity list + breakdown
// ring" card shell used across Campaign Details tabs (Creatives, Publishers,
// Segments, Referrers, Transaction Creatives, Transaction Publishers, Basket
// Items). 7+ consumers had the same layout duplicated: card header with
// title + optional head-right controls, optional tabs row, two-column body
// with a table on the left and an optional breakdown ring chart on the
// right.
//
// Layout contract (see SPECIFICATION.md §10):
//
//   ┌ head ─────────────────────────────────────────────────────┐
//   │ {title}       · {controls?}                                │
//   ├─ {tabs?} ──────────────────────────────────────────────────┤
//   │ ┌ {table} ───────────────────┬ {breakdown}? ─────────────┐ │
//   │ │                            │ ┌ breakdownTitle · ctrl? ┐ │
//   │ │                            │ │                        │ │
//   │ │                            │ │ {breakdownChart}       │ │
//   │ │                            │ └────────────────────────┘ │
//   │ └────────────────────────────┴────────────────────────────┘ │
//   └────────────────────────────────────────────────────────────┘
//
// Optional-breakdown: when neither `breakdownChart` nor `breakdownControl`
// is passed, the aside is omitted and the table takes the full body width.
// Loading: `loading` prop replaces the body with a v2 shimmer
// (`.skeleton` primitive) sized to mirror the final table/breakdown shape
// — matches the tab-level shimmer vocabulary everywhere else.
//
// Uses the `.campaign-overview__creatives-*` BEM scope from `_overview.scss`
// (historical name — the shell is entity-agnostic). A follow-up can rename
// to `.campaign-overview__entity-*` without an API change.
// =============================================================================

const EntityBreakdownCard = ({
  title,
  controls,
  search,
  tabs,
  table,
  breakdownTitle = 'Breakdown',
  breakdownControl,
  breakdownChart,
  loading = false,
  className,
}) => {
  const extraClass = className ? ` ${className}` : '';
  // `controls` is the preferred slot name; `search` kept as a backward-
  // compatible alias for consumers that already call it that. Both land
  // in the head's right slot.
  const headRight = controls !== undefined ? controls : search;
  const hasBreakdown = Boolean(breakdownChart || breakdownControl);

  return (
    <section className={`campaign-overview__creatives${extraClass}`}>
      <header className="campaign-overview__creatives-head">
        <div className="campaign-overview__creatives-eyebrow mono-eyebrow">
          {title}
        </div>
        {headRight}
      </header>

      {tabs}

      {loading ? (
        <div style={{ padding: 16 }}>
          <span
            className="skeleton"
            style={{ width: '100%', height: 220, borderRadius: 12, display: 'block' }}
          />
        </div>
      ) : (
        <div className="campaign-overview__creatives-body">
          <div className="campaign-overview__creatives-table">{table}</div>
          {hasBreakdown && (
            <aside className="campaign-overview__creatives-breakdown">
              <div className="campaign-overview__creatives-breakdown-head">
                <div className="campaign-overview__creatives-breakdown-eyebrow mono-eyebrow">
                  {breakdownTitle}
                </div>
                {breakdownControl}
              </div>
              {breakdownChart}
            </aside>
          )}
        </div>
      )}
    </section>
  );
};

EntityBreakdownCard.propTypes = {
  title: PropTypes.node.isRequired,
  controls: PropTypes.node,
  search: PropTypes.node,
  tabs: PropTypes.node,
  table: PropTypes.node.isRequired,
  breakdownTitle: PropTypes.node,
  breakdownControl: PropTypes.node,
  breakdownChart: PropTypes.node,
  loading: PropTypes.bool,
  className: PropTypes.string,
};

export default EntityBreakdownCard;
